import { createFileRoute } from "@tanstack/react-router";
import { ArrowRight, Filter, History, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { TraceDetail } from "@/components/m01/trace-detail";
import { PageHeader, type PageHelp } from "@/components/shared/page-help";
import { Button } from "@/components/ui/button";
import { Drawer } from "@/components/ui/drawer";
import { Select } from "@/components/ui/select";
import { type SessionRecentItem, useRecentSessions } from "@/lib/api/sessions";
import {
	eventLabel,
	finalActionLabel,
	intentLabel,
	intentRuleLabel,
	knowledgeSourceLabel,
	playbookLabel,
	qualityDecisionLabel,
	sampleLabel,
	serviceStageLabel,
} from "@/lib/product-labels";
import {
	SINGLE_STAGE_RUN_RECORDS_EVENT,
	type SingleStageRunRecord,
	readSingleStageRunRecords,
} from "@/lib/single-stage-run-records";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/debug")({
	component: DebugPage,
});

type RecordKind = "response" | "single_stage" | "evaluation" | "analysis";
type RecordKindFilter = RecordKind | "all";
type StatusFilter = "all" | "active" | "closed" | "handoff" | "success" | "partial" | "error";

type RuntimeRecord = {
	id: string;
	traceId: string | null;
	kind: RecordKind;
	kindLabel: string;
	sourceLabel: string;
	merchantId: string;
	customerId: string;
	intent: string;
	progress: string;
	status: string;
	statusLabel: string;
	startedAt: string;
	updatedAt: string;
	searchable: string;
	singleStageRecord?: SingleStageRunRecord;
};

const PAGE_SIZE = 10;

const KIND_OPTIONS: Array<{ value: RecordKindFilter; label: string }> = [
	{ value: "all", label: "全部记录" },
	{ value: "response", label: "应答链路" },
	{ value: "single_stage", label: "单环节运行" },
	{ value: "evaluation", label: "评测运行" },
	{ value: "analysis", label: "分析记录" },
];

const STATUS_OPTIONS: Array<{ value: StatusFilter; label: string }> = [
	{ value: "all", label: "全部状态" },
	{ value: "active", label: "处理中" },
	{ value: "success", label: "已完成" },
	{ value: "partial", label: "待补充" },
	{ value: "handoff", label: "转人工" },
	{ value: "closed", label: "已关闭" },
	{ value: "error", label: "调用失败" },
];

const RECORD_SCOPE = [
	{
		kind: "应答链路",
		status: "已接入",
		description: "链路测试台和真实会话沉淀 Trace，可还原输入、分类、方案、证据、回复和评测摘要。",
	},
	{
		kind: "单环节运行",
		status: "已接入",
		description:
			"分类、方案、工具、知识、提示词和质量策略在各能力页运行后会进入记录列表，详情还原输入、预期、结果和原始返回。",
	},
	{
		kind: "评测运行",
		status: "关联接入",
		description: "评测运营仍是主工作台；已关联到 Trace 的 Badcase 会在详情里显示摘要和关闭建议。",
	},
	{
		kind: "分析记录",
		status: "待接入",
		description: "后续运营复盘、归因结论和实验分析可挂到同一记录中心，当前先预留筛选口径。",
	},
];

function DebugPage() {
	return (
		<div className="mx-auto flex max-w-7xl flex-col gap-5">
			<PageHeader
				title="运行记录"
				description="检索链路测试台和真实会话留下的处理事实，按记录类型筛选，并在详情抽屉还原当时的输入、判断、证据、回复和评测摘要。"
				help={debugHelp}
			>
				<Button asChild variant="outline" size="sm">
					<a href="/pipeline-testbench">
						进入链路测试台
						<ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
					</a>
				</Button>
			</PageHeader>

			<RunRecordRelationship />
			<RecordScopeMatrix />
			<RunRecordExplorer />
		</div>
	);
}

function RunRecordRelationship() {
	return (
		<section
			className="grid gap-3 rounded-lg border border-surface-border bg-surface-card p-4 md:grid-cols-3"
			aria-label="运行记录与链路测试台关系"
		>
			<RelationshipStep
				index="1"
				title="链路测试台发起验证"
				description="输入一条客户问题，选择数据模式，跑端到端链路或从历史上下文复跑。"
			/>
			<RelationshipStep
				index="2"
				title="运行记录沉淀事实"
				description="记录本次 session、trace、意图、方案、运行能力、回复结果和关联评测摘要。"
			/>
			<RelationshipStep
				index="3"
				title="复盘后继续优化"
				description="从详情回到链路测试台复跑，或把问题交给评测运营、样本库和对应能力页处理。"
			/>
		</section>
	);
}

function RelationshipStep({
	index,
	title,
	description,
}: {
	index: string;
	title: string;
	description: string;
}) {
	return (
		<div className="flex gap-3">
			<div className="flex h-7 w-7 flex-none items-center justify-center rounded-md bg-brand-primary text-sm font-semibold text-text-inverse">
				{index}
			</div>
			<div className="space-y-1">
				<h3 className="text-sm font-semibold text-text-primary">{title}</h3>
				<p className="text-sm leading-6 text-text-secondary">{description}</p>
			</div>
		</div>
	);
}

function RecordScopeMatrix() {
	return (
		<section className="space-y-3" aria-labelledby="record-scope-title">
			<div className="flex items-center gap-2">
				<History className="h-4 w-4 text-brand-primary" aria-hidden="true" />
				<h3 id="record-scope-title" className="text-base font-semibold text-text-primary">
					统一记录口径
				</h3>
			</div>
			<div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
				{RECORD_SCOPE.map((item) => (
					<div
						key={item.kind}
						className="rounded-lg border border-surface-border bg-surface-card p-4"
					>
						<div className="flex items-center justify-between gap-2">
							<h4 className="text-sm font-semibold text-text-primary">{item.kind}</h4>
							<span className="rounded-full border border-surface-border bg-surface-subtle px-2 py-0.5 text-xs text-text-secondary">
								{item.status}
							</span>
						</div>
						<p className="mt-2 text-sm leading-6 text-text-secondary">{item.description}</p>
					</div>
				))}
			</div>
		</section>
	);
}

function RunRecordExplorer() {
	const { data, isLoading, isError, error } = useRecentSessions(50);
	const [singleStageRecords, setSingleStageRecords] = useState<SingleStageRunRecord[]>([]);
	const [selectedRecord, setSelectedRecord] = useState<RuntimeRecord | null>(null);
	const [keyword, setKeyword] = useState("");
	const [kindFilter, setKindFilter] = useState<RecordKindFilter>("all");
	const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
	const [intentFilter, setIntentFilter] = useState("all");
	const [currentPage, setCurrentPage] = useState(1);

	useEffect(() => {
		const refresh = () => setSingleStageRecords(readSingleStageRunRecords());
		refresh();
		window.addEventListener(SINGLE_STAGE_RUN_RECORDS_EVENT, refresh);
		window.addEventListener("storage", refresh);
		return () => {
			window.removeEventListener(SINGLE_STAGE_RUN_RECORDS_EVENT, refresh);
			window.removeEventListener("storage", refresh);
		};
	}, []);

	const responseRecords = useMemo(
		() => (data?.items ?? []).map((session) => buildRuntimeRecord(session)),
		[data?.items],
	);
	const singleStageRuntimeRecords = useMemo(
		() => singleStageRecords.map((record) => buildSingleStageRuntimeRecord(record)),
		[singleStageRecords],
	);
	const records = useMemo(
		() =>
			[...singleStageRuntimeRecords, ...responseRecords].sort((a, b) =>
				b.updatedAt.localeCompare(a.updatedAt),
			),
		[singleStageRuntimeRecords, responseRecords],
	);
	const intents = useMemo(() => uniqueIntents(records), [records]);
	const filteredRecords = useMemo(
		() => filterRecords(records, { keyword, kindFilter, statusFilter, intentFilter }),
		[records, keyword, kindFilter, statusFilter, intentFilter],
	);
	const totalPages = Math.max(1, Math.ceil(filteredRecords.length / PAGE_SIZE));
	const pageStart = filteredRecords.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
	const pageEnd = Math.min(currentPage * PAGE_SIZE, filteredRecords.length);
	const pagedRecords = useMemo(
		() => filteredRecords.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
		[filteredRecords, currentPage],
	);

	useEffect(() => {
		if (currentPage > totalPages) setCurrentPage(totalPages);
	}, [currentPage, totalPages]);

	if (isLoading) {
		return (
			<div data-testid="sessions-loading" className="rounded-lg border border-surface-border p-4">
				加载运行记录中...
			</div>
		);
	}

	if (isError) {
		return (
			<div
				data-testid="sessions-error"
				className="rounded-lg border border-brand-danger/30 bg-brand-danger/5 p-4 text-sm text-brand-danger"
			>
				加载失败：{error instanceof Error ? error.message : String(error)}
			</div>
		);
	}

	return (
		<section className="space-y-4" aria-labelledby="record-list-title">
			<div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
				<div className="space-y-1">
					<h3 id="record-list-title" className="text-base font-semibold text-text-primary">
						记录列表
					</h3>
					<p className="text-sm text-text-secondary">
						先用条件定位记录，再打开详情查看当时完整事实。意图、方案等环节事实在详情里按阶段还原。
					</p>
				</div>
				<div className="text-sm text-text-secondary">
					共 {records.length} 条，筛选后 {filteredRecords.length} 条
					{filteredRecords.length > 0 ? `，当前显示 ${pageStart}-${pageEnd} 条` : ""}
				</div>
			</div>

			<RecordFilters
				keyword={keyword}
				onKeywordChange={(value) => {
					setKeyword(value);
					setCurrentPage(1);
				}}
				kindFilter={kindFilter}
				onKindFilterChange={(value) => {
					setKindFilter(value);
					setCurrentPage(1);
				}}
				statusFilter={statusFilter}
				onStatusFilterChange={(value) => {
					setStatusFilter(value);
					setCurrentPage(1);
				}}
				intentFilter={intentFilter}
				onIntentFilterChange={(value) => {
					setIntentFilter(value);
					setCurrentPage(1);
				}}
				intents={intents}
			/>

			<RunRecordTable records={pagedRecords} onOpenRecord={setSelectedRecord} />
			<RunRecordPagination
				currentPage={currentPage}
				totalPages={totalPages}
				totalItems={filteredRecords.length}
				pageStart={pageStart}
				pageEnd={pageEnd}
				onPageChange={setCurrentPage}
			/>

			<Drawer
				open={selectedRecord !== null}
				onClose={() => setSelectedRecord(null)}
				title="运行详情"
				description={
					selectedRecord
						? `${selectedRecord.kindLabel} · ${selectedRecord.id} · ${selectedRecord.statusLabel}`
						: "查看一条历史运行的完整事实。"
				}
				width="min(980px,100vw)"
				testId="run-record-detail-drawer"
			>
				<RunRecordDetail record={selectedRecord} />
			</Drawer>
		</section>
	);
}

function RecordFilters({
	keyword,
	onKeywordChange,
	kindFilter,
	onKindFilterChange,
	statusFilter,
	onStatusFilterChange,
	intentFilter,
	onIntentFilterChange,
	intents,
}: {
	keyword: string;
	onKeywordChange: (value: string) => void;
	kindFilter: RecordKindFilter;
	onKindFilterChange: (value: RecordKindFilter) => void;
	statusFilter: StatusFilter;
	onStatusFilterChange: (value: StatusFilter) => void;
	intentFilter: string;
	onIntentFilterChange: (value: string) => void;
	intents: string[];
}) {
	return (
		<div className="grid gap-3 rounded-lg border border-surface-border bg-surface-card p-4 lg:grid-cols-[1.5fr_1fr_1fr_1fr]">
			<label className="space-y-1 text-sm">
				<span className="flex items-center gap-2 font-medium text-text-primary">
					<Search className="h-4 w-4 text-text-muted" aria-hidden="true" />
					搜索记录
				</span>
				<input
					value={keyword}
					onChange={(event) => onKeywordChange(event.target.value)}
					placeholder="记录 ID、商户、客户、意图、方案、样本"
					className="h-10 w-full rounded-md border border-surface-border bg-surface-background px-3 text-sm text-text-primary outline-none focus:border-brand-primary focus:ring-2 focus:ring-state-focus/30"
				/>
			</label>
			<div className="space-y-1 text-sm">
				<span className="flex items-center gap-2 font-medium text-text-primary">
					<Filter className="h-4 w-4 text-text-muted" aria-hidden="true" />
					记录类型
				</span>
				<Select
					className="w-full"
					aria-label="记录类型"
					value={kindFilter}
					onChange={(event) => onKindFilterChange(event.target.value as RecordKindFilter)}
				>
					{KIND_OPTIONS.map((option) => (
						<option key={option.value} value={option.value}>
							{option.label}
						</option>
					))}
				</Select>
			</div>
			<div className="space-y-1 text-sm">
				<span className="font-medium text-text-primary">运行状态</span>
				<Select
					className="w-full"
					aria-label="运行状态"
					value={statusFilter}
					onChange={(event) => onStatusFilterChange(event.target.value as StatusFilter)}
				>
					{STATUS_OPTIONS.map((option) => (
						<option key={option.value} value={option.value}>
							{option.label}
						</option>
					))}
				</Select>
			</div>
			<div className="space-y-1 text-sm">
				<span className="font-medium text-text-primary">识别意图</span>
				<Select
					className="w-full"
					aria-label="识别意图"
					value={intentFilter}
					onChange={(event) => onIntentFilterChange(event.target.value)}
				>
					<option value="all">全部意图</option>
					{intents.map((intent) => (
						<option key={intent} value={intent}>
							{intent}
						</option>
					))}
				</Select>
			</div>
		</div>
	);
}

function RunRecordTable({
	records,
	onOpenRecord,
}: {
	records: RuntimeRecord[];
	onOpenRecord: (record: RuntimeRecord) => void;
}) {
	if (records.length === 0) {
		return (
			<div
				data-testid="sessions-empty"
				className="rounded-lg border border-surface-border bg-surface-card p-8 text-center text-sm text-text-secondary"
			>
				当前条件下暂无运行记录。可放宽筛选条件，或先到链路测试台发起一次测试。
			</div>
		);
	}

	return (
		<div
			className="overflow-hidden rounded-lg border border-surface-border bg-surface-card"
			data-testid="sessions-list"
		>
			<div className="overflow-x-auto">
				<table className="min-w-full divide-y divide-surface-border text-sm">
					<thead className="bg-surface-subtle text-left text-xs font-semibold uppercase text-text-muted">
						<tr>
							<th className="px-4 py-3">更新时间</th>
							<th className="px-4 py-3">记录类型</th>
							<th className="px-4 py-3">记录 ID</th>
							<th className="px-4 py-3">意图 / 阶段</th>
							<th className="px-4 py-3">商户 / 客户</th>
							<th className="px-4 py-3">状态</th>
							<th className="px-4 py-3 text-right">操作</th>
						</tr>
					</thead>
					<tbody className="divide-y divide-surface-border">
						{records.map((record) => (
							<tr key={record.id} className="hover:bg-surface-subtle/70">
								<td className="whitespace-nowrap px-4 py-3 text-text-secondary">
									{formatDateTime(record.updatedAt)}
								</td>
								<td className="px-4 py-3">
									<div className="font-medium text-text-primary">{record.kindLabel}</div>
									<div className="mt-0.5 text-xs text-text-muted">{record.sourceLabel}</div>
								</td>
								<td className="px-4 py-3">
									<div className="font-mono text-xs text-text-primary">{record.id}</div>
								</td>
								<td className="px-4 py-3 text-text-secondary">
									<div>{record.intent}</div>
									<div className="mt-0.5 text-xs text-text-muted">{record.progress}</div>
								</td>
								<td className="px-4 py-3 text-text-secondary">
									<div>{record.merchantId}</div>
									<div className="mt-0.5 text-xs text-text-muted">{record.customerId}</div>
								</td>
								<td className="px-4 py-3">
									<span
										className={cn(
											"inline-flex rounded-full border px-2 py-0.5 text-xs font-medium",
											statusBadgeClass(record.status),
										)}
									>
										{record.statusLabel}
									</span>
								</td>
								<td className="px-4 py-3 text-right">
									<Button
										type="button"
										variant="outline"
										size="sm"
										onClick={() => onOpenRecord(record)}
									>
										查看 {record.id} 详情
									</Button>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
}

function RunRecordPagination({
	currentPage,
	totalPages,
	totalItems,
	pageStart,
	pageEnd,
	onPageChange,
}: {
	currentPage: number;
	totalPages: number;
	totalItems: number;
	pageStart: number;
	pageEnd: number;
	onPageChange: (page: number) => void;
}) {
	if (totalItems === 0) return null;
	return (
		<div
			className="flex flex-col gap-2 rounded-lg border border-surface-border bg-surface-card px-4 py-3 text-sm text-text-secondary sm:flex-row sm:items-center sm:justify-between"
			data-testid="run-record-pagination"
		>
			<div>
				第 {currentPage} / {totalPages} 页，显示 {pageStart}-{pageEnd} / {totalItems} 条
			</div>
			<div className="flex items-center gap-2">
				<Button
					type="button"
					variant="outline"
					size="sm"
					disabled={currentPage <= 1}
					onClick={() => onPageChange(Math.max(1, currentPage - 1))}
				>
					上一页
				</Button>
				<Button
					type="button"
					variant="outline"
					size="sm"
					disabled={currentPage >= totalPages}
					onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
				>
					下一页
				</Button>
			</div>
		</div>
	);
}

function RunRecordDetail({ record }: { record: RuntimeRecord | null }) {
	if (!record) {
		return (
			<div className="rounded-lg border border-surface-border bg-surface-card p-4 text-sm text-text-secondary">
				请选择一条运行记录。
			</div>
		);
	}
	if (record.kind === "single_stage" && record.singleStageRecord) {
		return <SingleStageRecordDetail record={record.singleStageRecord} />;
	}
	return <TraceDetail traceId={record.traceId} />;
}

function SingleStageRecordDetail({ record }: { record: SingleStageRunRecord }) {
	return (
		<div className="space-y-4" data-testid="single-stage-record-detail">
			<section className="rounded-lg border border-surface-border bg-surface-card p-4">
				<div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
					<div>
						<h3 className="text-base font-semibold text-text-primary">单环节运行详情</h3>
						<p className="mt-1 text-sm leading-6 text-text-secondary">
							这条记录来自能力页测试，用来还原当时输入、预期、实际返回和原始响应。
						</p>
					</div>
					<span
						className={cn(
							"w-fit rounded-full border px-2 py-0.5 text-xs font-medium",
							statusBadgeClass(record.status),
						)}
					>
						{statusLabel(record.status)}
					</span>
				</div>
				<div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
					<DetailFact label="记录 ID" value={record.id} mono />
					<DetailFact label="验证环节" value={record.targetStageLabel} />
					<DetailFact label="代表样本" value={sampleLabel(record.sampleId)} />
					<DetailFact label="更新时间" value={formatDateTime(record.updatedAt)} />
				</div>
			</section>

			<section className="grid gap-4 xl:grid-cols-[1fr_1fr]">
				<div className="rounded-lg border border-surface-border bg-surface-card p-4">
					<h4 className="text-sm font-semibold text-text-primary">模拟客户问题</h4>
					<p className="mt-2 whitespace-pre-wrap rounded-md border border-surface-border bg-surface-subtle p-3 text-sm leading-6 text-text-secondary">
						{record.message}
					</p>
					<div className="mt-3 grid gap-3 sm:grid-cols-2">
						<DetailFact label="预期分类" value={intentLabel(record.expectedIntent)} />
						<DetailFact label="预期方案" value={playbookLabel(record.expectedPlaybook)} />
					</div>
				</div>
				<div className="rounded-lg border border-surface-border bg-surface-card p-4">
					<h4 className="text-sm font-semibold text-text-primary">{record.title}</h4>
					<div className="mt-3 grid gap-3 sm:grid-cols-2">
						{record.summary.map((item) => (
							<DetailFact
								key={item.label}
								label={item.label}
								value={summaryDisplayValue(item.label, item.value)}
							/>
						))}
					</div>
				</div>
			</section>

			<section className="rounded-lg border border-surface-border bg-surface-card p-4">
				<h4 className="text-sm font-semibold text-text-primary">资产与关联</h4>
				<div className="mt-3 grid gap-3 lg:grid-cols-3">
					<DetailFact label="测试资产" value={record.assetName} />
					<DetailFact label="商户" value={record.merchantId} mono />
					<DetailFact label="客户" value={record.customerId} />
				</div>
				<p className="mt-3 rounded-md border border-surface-border bg-surface-subtle p-3 text-sm leading-6 text-text-secondary">
					{record.boundary}
				</p>
				{record.traceId || record.runId || record.sessionId ? (
					<div className="mt-3 grid gap-3 lg:grid-cols-3">
						<DetailFact label="Trace ID" value={record.traceId ?? "未生成"} mono />
						<DetailFact label="Run ID" value={record.runId ?? "未生成"} mono />
						<DetailFact label="Session ID" value={record.sessionId ?? "未生成"} mono />
					</div>
				) : null}
			</section>

			<details className="rounded-lg border border-surface-border bg-surface-card p-4">
				<summary className="cursor-pointer text-sm font-semibold text-text-primary">
					原始返回
				</summary>
				<pre className="mt-3 max-h-[420px] overflow-auto whitespace-pre-wrap break-words rounded-md bg-surface-subtle p-3 text-xs leading-5 text-text-secondary">
					{JSON.stringify(record.raw, null, 2)}
				</pre>
			</details>
		</div>
	);
}

function DetailFact({
	label,
	value,
	mono = false,
}: {
	label: string;
	value: string;
	mono?: boolean;
}) {
	return (
		<div className="rounded-md border border-surface-border bg-surface-background px-3 py-2">
			<div className="text-xs font-medium text-text-muted">{label}</div>
			<div
				className={cn(
					"mt-1 break-words text-sm font-semibold leading-5 text-text-primary",
					mono && "font-mono text-xs",
				)}
			>
				{value}
			</div>
		</div>
	);
}

function summaryDisplayValue(label: string, value: string): string {
	if (label === "实际分类" || label === "意图") return intentLabel(value);
	if (label === "预期分类") return intentLabel(value);
	if (label === "命中方案" || label === "方案") return playbookLabel(value);
	if (label === "预期方案") return playbookLabel(value);
	if (label === "规则命中" || label === "规则") return intentRuleLabel(value);
	if (label === "服务阶段") return serviceStageLabel(value);
	if (label === "生成事件" || label === "质量事件") return eventLabel(value);
	if (label === "质量判定") return qualityDecisionLabel(value);
	if (label === "最终动作") return finalActionLabel(value);
	if (label === "来源") return knowledgeSourceLabel(value);
	return value;
}

function buildRuntimeRecord(session: SessionRecentItem): RuntimeRecord {
	const status = normalizedStatus(session);
	const rawIntent = session.intent ?? "unknown";
	const intent = intentLabel(rawIntent);
	const progress = progressionLabel(session.progression_state);
	const customerId = session.customer_id ?? "未返回";
	const searchable = [
		session.session_id,
		session.merchant_id,
		customerId,
		rawIntent,
		intent,
		session.progression_state,
		session.session_status,
	].join(" ");
	return {
		id: session.session_id,
		traceId: session.session_id,
		kind: "response",
		kindLabel: "应答链路",
		sourceLabel: "Trace / Session",
		merchantId: session.merchant_id,
		customerId,
		intent,
		progress,
		status,
		statusLabel: statusLabel(status),
		startedAt: session.start_time,
		updatedAt: session.last_activity_time,
		searchable: searchable.toLowerCase(),
	};
}

function buildSingleStageRuntimeRecord(record: SingleStageRunRecord): RuntimeRecord {
	const rawIntent = record.actualIntent || record.expectedIntent || "unknown";
	const intent = intentLabel(rawIntent);
	const progress = `${record.targetStageLabel} · ${sampleLabel(record.sampleId)}`;
	const searchable = [
		record.searchable,
		record.sampleId,
		sampleLabel(record.sampleId),
		record.expectedIntent,
		intentLabel(record.expectedIntent),
		record.expectedPlaybook,
		playbookLabel(record.expectedPlaybook),
		record.actualIntent,
		intent,
		record.actualPlaybook,
		playbookLabel(record.actualPlaybook),
	].join(" ");
	return {
		id: record.id,
		traceId: record.traceId,
		kind: "single_stage",
		kindLabel: "单环节运行",
		sourceLabel: "能力页测试",
		merchantId: record.merchantId,
		customerId: record.customerId,
		intent,
		progress,
		status: record.status,
		statusLabel: statusLabel(record.status),
		startedAt: record.createdAt,
		updatedAt: record.updatedAt,
		searchable: searchable.toLowerCase(),
		singleStageRecord: record,
	};
}

function filterRecords(
	records: RuntimeRecord[],
	filters: {
		keyword: string;
		kindFilter: RecordKindFilter;
		statusFilter: StatusFilter;
		intentFilter: string;
	},
) {
	const keyword = filters.keyword.trim().toLowerCase();
	return records.filter((record) => {
		if (filters.kindFilter !== "all" && record.kind !== filters.kindFilter) return false;
		if (filters.statusFilter !== "all" && record.status !== filters.statusFilter) return false;
		if (filters.intentFilter !== "all" && record.intent !== filters.intentFilter) return false;
		if (keyword.length > 0 && !record.searchable.includes(keyword)) return false;
		return true;
	});
}

function uniqueIntents(records: RuntimeRecord[]) {
	return Array.from(new Set(records.map((record) => record.intent))).sort((a, b) =>
		a.localeCompare(b),
	);
}

function normalizedStatus(session: SessionRecentItem): StatusFilter {
	if (session.progression_state === "handoff") return "handoff";
	if (session.progression_state === "closing") return "success";
	if (session.progression_state === "need_gathering") return "partial";
	if (session.session_status === "closed") return "closed";
	return "active";
}

function statusLabel(status: string): string {
	if (status === "success") return "已完成";
	if (status === "partial") return "待补充";
	if (status === "handoff") return "转人工";
	if (status === "closed") return "已关闭";
	if (status === "error") return "调用失败";
	return "处理中";
}

function statusBadgeClass(status: string): string {
	if (status === "success") return "border-brand-success/30 bg-brand-success/10 text-brand-success";
	if (status === "partial") return "border-brand-warning/30 bg-brand-warning/10 text-brand-warning";
	if (status === "handoff") return "border-brand-danger/30 bg-brand-danger/10 text-brand-danger";
	if (status === "closed") return "border-surface-border bg-surface-muted text-text-secondary";
	if (status === "error") return "border-brand-danger/30 bg-brand-danger/10 text-brand-danger";
	return "border-brand-primary/30 bg-brand-primary/10 text-brand-primary";
}

function progressionLabel(value: string): string {
	if (value === "closing") return "收口阶段";
	if (value === "need_gathering") return "待补充信息";
	if (value === "handoff") return "人工承接";
	return value || "未返回";
}

function formatDateTime(value: string): string {
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return value;
	return new Intl.DateTimeFormat("zh-CN", {
		month: "2-digit",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
	}).format(date);
}

const debugHelp: PageHelp = {
	title: "运行记录说明",
	description: "这里是运行事实的统一入口，不替代链路测试台，而是承接它产生的历史记录。",
	sections: [
		{
			title: "和链路测试台的关系",
			items: [
				"链路测试台用于发起一次测试或复跑；运行记录用于查找这次测试留下的历史事实。",
				"详情抽屉复用链路测试台的运行详情结构，让同一条记录能看输入、分类、方案、证据、回复和审计。",
				"如果发现结果异常，可以从详情继续带上下文回到链路测试台复跑。",
			],
		},
		{
			title: "当前能看哪些记录",
			items: [
				"已接入应答链路记录和能力页单环节运行记录：应答链路来自 recent sessions 和 trace detail，单环节运行来自本机浏览器记录。",
				"意图分类、处理方案、工具、知识、提示词和质量策略在能力页运行后，会作为单环节运行记录进入列表。",
				"评测运行记录和运营分析记录仍先在各自页面承接；后续再补服务端分页、长期留存和跨人协作入口。",
			],
		},
	],
};
